// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { motion } from "framer-motion";
import { FiSun, FiMoon, FiMenu, FiX, FiMail } from "react-icons/fi";
import logo from "../../assets/logo_500x500px_ei_harsoa.webp";

const navItems = [
  { label: "Meistä", to: "meista" },
  { label: "Ohjelmisto", to: "ohjelmisto" },
  { label: "Media", to: "media" },
  { label: "Esiintymiset", to: "esiintymiset" },
  { label: "UKK", to: "useinkysyttya" },
];

export default function Navbar() {
  const [theme, setTheme] = useState("dark");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const contactUrl =
    "https://spiderlegsproductions.com/esiintyja/bilebandijoyride";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 bg-white dark:bg-joyrideBars transition-shadow duration-300 ${
          scrolled ? "shadow-md" : ""
        }`}
        role="navigation"
        aria-label="Päävalikko"
      >
        <div className="container flex items-center justify-between h-16 px-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" aria-label="Siirry etusivulle">
              <img
                src={logo}
                alt="Bilebändi Joyride logo"
                className="w-28 h-auto"
                width="112"
                height="112"
              />
            </Link>
          </div>

          {/* Desktop-navigaatio */}
          <div className="hidden md:flex space-x-6">
            {navItems.map(({ label, to }) => (
              <HashLink
                key={to}
                to={`/#${to}`}
                aria-label={`Siirry osioon ${label}`}
                scroll={(el) => {}}
                className="relative text-joyrideNavy dark:text-joyrideLight hover:text-joyrideCyan dark:hover:text-joyrideCyan transition cursor-pointer"
              >
                {label}
                <motion.div
                  className="absolute left-0 bottom-0 h-0.5 bg-joyrideCyan"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ originX: 0 }}
                />
              </HashLink>
            ))}
          </div>

          {/* Oikean reunan napit */}
          <div className="flex items-center space-x-4">
            {/* Teeman vaihtaja */}
            <button
              onClick={() =>
                setTheme((prev) => (prev === "light" ? "dark" : "light"))
              }
              aria-label="Vaihda teemaa"
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              {theme === "light" ? (
                <FiMoon className="text-joyrideNavy" size={20} />
              ) : (
                <FiSun className="text-joyrideLight" size={20} />
              )}
            </button>

            {/* Varaa (desktop) */}
            <motion.a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="hidden md:flex items-center gap-2 px-4 py-2 text-joyrideNavy dark:text-joyrideNavy font-semibold text-sm rounded-full shadow-lg bg-joyrideSky transition-all duration-300"
            >
              <FiMail size={18} /> <span>Varaa</span>
            </motion.a>

            {/* Mobile-valikko (menu) */}
            <button
              onClick={() => setOpen((o) => !o)}
              className="md:hidden p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
              aria-label={open ? "Sulje valikko" : "Avaa valikko"}
            >
              {open ? (
                <FiX
                  size={24}
                  className="text-joyrideNavy dark:text-joyrideLight"
                />
              ) : (
                <FiMenu
                  size={24}
                  className="text-joyrideNavy dark:text-joyrideLight"
                />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Scroll progress bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-joyrideCyan z-[60] transition-all duration-200"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Mobile-valikko */}
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="md:hidden backdrop-blur-lg bg-white/80 dark:bg-joyrideNavy/80 shadow-lg border-b border-gray-200 dark:border-gray-900"
          role="dialog"
          aria-label="Mobiilivalikko"
        >
          <div className="flex flex-col items-center space-y-4 py-4 mt-16">
            {navItems.map(({ label, to }) => (
              <HashLink
                key={to}
                to={`/#${to}`}
                onClick={() => setOpen(false)}
                aria-label={`Siirry osioon ${label}`}
                scroll={(el) => {}}
                className="text-joyrideNavy dark:text-joyrideLight text-lg cursor-pointer hover:text-joyrideCyan dark:hover:text-joyrideCyan transition"
              >
                {label}
              </HashLink>
            ))}

            <motion.a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-6 py-2 text-joyrideNavy font-semibold text-sm rounded-full shadow-lg dark:text-joyrideNavy bg-joyrideSky transition-all duration-300"
            >
              <FiMail size={18} /> <span>Varaa esiintyminen!</span>
            </motion.a>
          </div>
        </motion.div>
      )}
    </>
  );
}
